// Stable, well-spread colour per operator so every building visibly carries its
// author's identity at a glance, IDENTICALLY across the two render paths:
//   - the editable google.maps.Polygon layer (CSS hsl string), and
//   - the deck.gl MVT layer (RGB triple).
// Both derive from the SAME hue hash so an operator's footprints don't change
// colour when the map switches between the two layers at the density threshold.
// Saturation/lightness are fixed at values that stay legible over satellite and
// roadmap tiles alike.

export function operatorHue(id) {
  const s = String(id || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % 360;
}

export function operatorCssColor(id) {
  return `hsl(${operatorHue(id)}, 72%, 55%)`;
}

// hue 0..360, s/l in percent -> [r,g,b] 0..255. Standard HSL->RGB (the same
// transform the browser applies to the hsl() string above), so operatorRgb and
// operatorCssColor render the identical colour.
function hslToRgb(h, s, l) {
  const sn = s / 100;
  const ln = l / 100;
  const a = sn * Math.min(ln, 1 - ln);
  const f = (n) => {
    const k = (n + h / 30) % 12;
    return ln - a * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return [Math.round(255 * f(0)), Math.round(255 * f(8)), Math.round(255 * f(4))];
}

export function operatorRgb(id) {
  return hslToRgb(operatorHue(id), 72, 55);
}

function hexRgb(value) {
  const match = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(String(value || ''));
  return match ? match.slice(1).map((channel) => parseInt(channel, 16)) : null;
}

function relativeLuminance(value) {
  const rgb = hexRgb(value);
  if (!rgb) return null;
  return rgb
    .map((channel) => channel / 255)
    .map((channel) => (channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4))
    .reduce((sum, channel, index) => sum + channel * [0.2126, 0.7152, 0.0722][index], 0);
}

export function operatorContrastRatio(background, foreground) {
  const backgroundLuminance = relativeLuminance(background);
  const foregroundLuminance = relativeLuminance(foreground);
  if (backgroundLuminance === null || foregroundLuminance === null) return 0;
  const lighter = Math.max(backgroundLuminance, foregroundLuminance);
  const darker = Math.min(backgroundLuminance, foregroundLuminance);
  return (lighter + 0.05) / (darker + 0.05);
}

export function operatorColorsMeetContrast(background, foreground) {
  return operatorContrastRatio(background, foreground) >= 4.5;
}
